import _extends from "@babel/runtime/helpers/extends";
import React, { useState } from "react";
import { Alert, Badge, Box, Button, Card, Grid, Icon, Picture, Spinner, Text } from "~/components-shared";
import styled, { useTheme } from "styled-components";
import { AuthoritiesEnum, IntegrationTypeEnum, MemberDepartmentEnum, MemberLevelEnum, StatusStateEnum } from "~/types/enums";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import lists from "~/lib/lists";
import { TrendReportActionEnum } from "~/store/trendReports/types";
import { usePostHog } from "posthog-js/react";
import trendReportsActionCreators from "~/store/trendReports/actions";
import toastsActionCreators from "~/store/toasts/actions";
import trendReportSelectors from "~/store/trendReports/selectors";
import useAbacExt from "~/hooks/abac";
import integrationSelectors from "~/store/integrations/selectors";
import authSelectors from "~/store/auth/selectors";
import { genericColor } from "~/lib/resolvers";
import { helpers } from "~/lib/helpers";
import Dropdown from "../../components-shared/Dropdown";
const Actions = styled(Box)`
  margin: -10px -10px 0 5px;
  float: right;
`;
const Description = styled(Text)`
  flex-grow: 1;
  width: 100%;
  overflow: auto;
`;
const Dot = styled.div`
  width: ${_ref => {
  let {
    $selected
  } = _ref;
  return $selected ? "30px" : "16px";
}};
  height: 16px;
  padding: 4px;
  border-radius: 8px;
  position: relative;
  transition: all 300ms ease-in-out;
  z-index: 1;

  &:after {
    content: "";
    display: block;
    border-radius: 8px;
    width: 100%;
    height: 100%;
    background-color: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return genericColor(theme, "translucentPrimary");
}};
  }

  &:hover:after {
    background-color: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return genericColor(theme, "primary");
}};
  }
`;
const StyledSource = styled(Picture)`
  background-color: ${_ref4 => {
  let {
    theme
  } = _ref4;
  return theme.colors.background.primary;
}};
  background-size: 80%;
`;
const Contributions = styled.div`
  background: ${_ref5 => {
  let {
    theme
  } = _ref5;
  return theme.colors.primary.light;
}};
  width: calc(100% + 2.8em);
  padding: 1em 1em 1.5em;
  text-align: center;
  margin: 0 -1.4em;
  border-bottom-left-radius: ${_ref6 => {
  let {
    theme
  } = _ref6;
  return theme.shape.borderRadius.primary;
}};
  border-bottom-right-radius: ${_ref7 => {
  let {
    theme
  } = _ref7;
  return theme.shape.borderRadius.primary;
}};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;
const Cap = styled.div`
  display: inline-block;
  width: ${_ref8 => {
  let {
    theme
  } = _ref8;
  return 6 * theme.typography.fontStep;
}}rem;
  min-width: ${_ref9 => {
  let {
    theme
  } = _ref9;
  return 6 * theme.typography.fontStep;
}}rem;
  height: ${_ref10 => {
  let {
    theme
  } = _ref10;
  return 6 * theme.typography.fontStep;
}}rem;
  text-align: center;
  line-height: ${_ref11 => {
  let {
    theme
  } = _ref11;
  return 6 * theme.typography.fontStep;
}}rem;
  border-radius: 50%;
  background-color: ${_ref12 => {
  let {
    theme
  } = _ref12;
  return theme.colors.primary.light;
}};
  position: relative;
  font-weight: ${_ref13 => {
  let {
    theme
  } = _ref13;
  return theme.typography.fontWeight.semiBold;
}};
  font-size: ${_ref14 => {
  let {
    theme
  } = _ref14;
  return 3 * theme.typography.fontStep;
}}rem;
  color: ${_ref15 => {
  let {
    theme
  } = _ref15;
  return theme.colors.primary.main;
}};
  box-shadow: 0 0 0 2px ${_ref16 => {
  let {
    theme
  } = _ref16;
  return theme.colors.background.primary;
}};
`;
const TrendingTopicCard = _ref17 => {
  let {
    reportId,
    trend,
    hideActions = false,
    ...rest
  } = _ref17;
  const {
    t
  } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const followStatus = useSelector(trendReportSelectors.getStatusByTrendFollow(trend.id));
  const followError = useSelector(trendReportSelectors.getErrorByTrendFollow(trend.id));
  const markStatus = useSelector(trendReportSelectors.getStatusByMarkForInsights(trend.id));
  const markError = useSelector(trendReportSelectors.getErrorByMarkForInsights(trend.id));
  const [hoverFollow, setHoverFollow] = useState(false);
  const error = followError || markError;
  const posthog = usePostHog();
  const {
    userHas
  } = useAbacExt();
  const sendStatus = useSelector(trendReportSelectors.getStatusByTrendSend(trend.id));
  const slackIntegration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.SLACK));
  const user = useSelector(authSelectors.getUser);
  const matrix = userHas(AuthoritiesEnum.EXPERIMENTAL) ? {
    technicality: 1,
    detail: 0
  } : lists.members.quadrant.matrix[`${user.department || MemberDepartmentEnum.PRODUCT}|${user.level || MemberLevelEnum.IC}`];
  const initialStyle = trend.styles?.find(style => style.technicality === matrix.technicality && style.detail === matrix.detail) || undefined;
  const [selectedStyle, setSelectedStyle] = useState(initialStyle);
  const handleFollowClick = () => {
    if (trend.theme.topic?.isFollowed) {
      dispatch(trendReportsActionCreators.unfollowTrendStarted(reportId, trend.id));
      posthog.capture(`Topic Trend – Unfollow "${trend.theme.name}"`, {
        reportId,
        trendId: trend.id
      });
    } else {
      dispatch(trendReportsActionCreators.followTrendStarted(reportId, trend.id, _ref18 => {
        let {
          data
        } = _ref18;
        if (data) {
          dispatch(toastsActionCreators.pushed({
            severity: "success",
            message: t("TopicTrendCard.successfullyFollowed"),
            timeout: 10000
          }));
        }
      }));
      posthog.capture(`Topic Trend – Follow "${trend.theme.name}"`, {
        reportId,
        trendId: trend.id
      });
    }
  };
  const handleDelete = () => {
    dispatch(trendReportsActionCreators.actionChanged(TrendReportActionEnum.DELETE_TREND, reportId, trend.id));
    posthog.capture(`Topic Trend – Delete "${trend.theme.name}"`, {
      reportId,
      trendId: trend.id
    });
  };
  const handleDismiss = () => {
    dispatch(trendReportsActionCreators.actionChanged(TrendReportActionEnum.DISMISS_TREND, reportId, trend.id));
    posthog.capture(`Topic Trend – Dismiss "${trend.theme.name}"`, {
      reportId,
      trendId: trend.id
    });
  };
  const handleShareInSlack = () => {
    dispatch(trendReportsActionCreators.actionChanged(TrendReportActionEnum.SHARE_TREND_IN_SLACK, reportId, trend.id));
    posthog.capture(`Topic Trend – Share in Slack "${trend.theme.name}"`, {
      reportId,
      trendId: trend.id
    });
  };
  const handleSendInSlack = callback => {
    dispatch(trendReportsActionCreators.sendTrendSlackStarted(reportId, trend.id, _ref19 => {
      let {
        data
      } = _ref19;
      if (data === trend.id) {
        dispatch(toastsActionCreators.pushed({
          severity: "success",
          message: t("TrendingTopicCard.reportSent"),
          timeout: 10000
        }));
        callback();
      }
    }));
    posthog.capture(`Topic Trend – Send in Slack "${trend.theme.name}"`, {
      reportId,
      trendId: trend.id
    });
  };
  const handleOverview = () => {
    dispatch(trendReportsActionCreators.actionChanged(TrendReportActionEnum.OVERVIEW, reportId, trend.id));
    posthog.capture(`Topic Trend – Overview "${trend.theme.name}"`, {
      reportId,
      trendId: trend.id
    });
  };
  const handleToggleSelectForInsights = () => {
    if (trend.isCustomerRelated) {
      dispatch(trendReportsActionCreators.unmarkForInsightsStarted(reportId, trend.id, _ref20 => {
        let {
          data
        } = _ref20;
        if (data) {
          dispatch(toastsActionCreators.pushed({
            severity: "success",
            message: t("TrendingTopicCard.successfullyUnmarkedForInsights", {
              name: selectedStyle?.name || trend.theme.name
            }),
            timeout: 10000
          }));
        }
      }));
    } else {
      dispatch(trendReportsActionCreators.markForInsightsStarted(reportId, trend.id, _ref21 => {
        let {
          data
        } = _ref21;
        if (data) {
          dispatch(toastsActionCreators.pushed({
            severity: "success",
            message: t("TrendingTopicCard.successfullyMarkedForInsights", {
              name: selectedStyle?.name || trend.theme.name
            }),
            timeout: 10000
          }));
        }
      }));
    }
    posthog.capture(`Topic Trend – Select for Insights "${trend.theme.name}"`, {
      reportId,
      trendId: trend.id
    });
  };
  const descArray = helpers.splitSentences(selectedStyle?.description || trend.theme.description || "");
  return /*#__PURE__*/React.createElement(Card, _extends({
    s: {
      p: 5,
      pb: 0
    },
    align: "left",
    minHeight: "420px",
    onClick: handleOverview,
    color: "background",
    shadow: true
  }, rest), /*#__PURE__*/React.createElement(Text, {
    element: "h4",
    s: {
      mb: 4
    },
    textOverflow: true
  }, /*#__PURE__*/React.createElement(Actions, {
    align: "center"
  }, /*#__PURE__*/React.createElement(Dropdown, {
    direction: "bottomLeft",
    handlerProps: {
      s: {
        p: 2
      },
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options")
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      src: "dots-vertical",
      color: theme.colors.text.primary,
      display: "block"
    })
  }, _ref22 => {
    let {
      isOpen,
      onItemClick
    } = _ref22;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, !!slackIntegration && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleSendInSlack(() => {
          onItemClick();
        });
      }
    }, sendStatus === StatusStateEnum.LOADING ? /*#__PURE__*/React.createElement(Spinner, {
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }) : /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "send",
      s: {
        mr: 2
      },
      color: "primary"
    }), t("TopicInfoCard.sendToSlack")), !!slackIntegration && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleShareInSlack();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "share",
      s: {
        mr: 2
      },
      color: "primary"
    }), t("TopicInfoCard.shareInSlack")), /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleDismiss();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: "cross",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }), t("common.actions.dismiss"), /*#__PURE__*/React.createElement(Icon, {
      s: {
        ml: 1
      },
      color: "primary",
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-html": "<strong>Hide</strong> Trending topic from your report",
      src: "infoOutline",
      style: {
        cursor: "help"
      }
    })), userHas([AuthoritiesEnum.EXPERIMENTAL]) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleToggleSelectForInsights();
        onItemClick();
      }
    }, markStatus === StatusStateEnum.LOADING ? /*#__PURE__*/React.createElement(Spinner, {
      scale: 4,
      color: "primary",
      s: {
        mr: 2
      }
    }) : /*#__PURE__*/React.createElement(Icon, {
      src: trend.isCustomerRelated ? "bookmarkCheckFill" : "bookmark",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }), trend.isCustomerRelated ? t("TrendingTopicCard.unmarkForInsights") : t("TrendingTopicCard.markForInsights"), /*#__PURE__*/React.createElement(Icon, {
      src: "encrypted",
      color: "primary",
      s: {
        ml: 1
      }
    })), /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleDelete();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: "bin",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "danger"
    }), t("common.actions.delete"), /*#__PURE__*/React.createElement(Icon, {
      src: "encrypted",
      color: "primary",
      s: {
        ml: 1
      }
    }))));
  })), /*#__PURE__*/React.createElement(Box, {
    gap: 1,
    s: {
      mb: 4,
      mx: -2
    }
  }, trend.isCustomerRelated && /*#__PURE__*/React.createElement(Badge, {
    lineHeight: 4,
    color: "translucentPrimary",
    s: {
      px: 2,
      py: 1
    },
    rounded: true
  }, "Customer"), /*#__PURE__*/React.createElement(Badge, {
    fontSize: 3,
    lineHeight: 4,
    color: lists.trends.workTypes.genericColors[trend.workType],
    s: {
      px: 3,
      py: 1
    },
    rounded: true
  }, lists.trends.workTypes.names[trend.workType])), selectedStyle?.name || trend.theme.name), /*#__PURE__*/React.createElement(Description, {
    element: "p",
    s: {
      mb: 4
    },
    color: "textSecondary",
    textOverflow: true
  }, descArray[0]), userHas([AuthoritiesEnum.EXPERIMENTAL]) && /*#__PURE__*/React.createElement(Box, {
    fullWidth: true,
    gap: 0,
    justify: "center",
    s: {
      my: 3
    }
  }, trend.styles.sort((a, b) => {
    const nameA = `${a.technicality}${a.detail}`;
    const nameB = `${b.technicality}${b.detail}`;
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0;
  }).map(style => /*#__PURE__*/React.createElement(Dot, {
    key: style.id,
    $selected: style.id === selectedStyle?.id,
    onClick: e => {
      e.stopPropagation();
      setSelectedStyle(style);
    },
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": `Technicality: ${style.technicality}. Detail: ${style.detail}`
  }))), /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    gap: 3,
    template: "auto auto",
    justify: "space-between",
    fullWidth: true,
    s: {
      mb: 4
    }
  }, trend.revenue?.total ? /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    fontSize: 3,
    color: "textSecondary",
    display: "block",
    weight: "regular",
    s: {
      mb: 2
    }
  }, t("TopicTrendCard.revenue")), /*#__PURE__*/React.createElement(Badge, {
    color: "primaryAccentContrast",
    display: "inline",
    fontSize: 3.5,
    weight: "semiBold",
    s: {
      py: 1,
      px: 3
    }
  }, trend.revenue?.total)) : /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    fontSize: 3,
    color: "textSecondary",
    display: "block",
    weight: "regular",
    s: {
      mb: 2
    }
  }, t("TopicTrendCard.contributors")), /*#__PURE__*/React.createElement(Box, {
    align: "center"
  }, trend.contributors.length ? /*#__PURE__*/React.createElement(Text, {
    fontSize: 4.5,
    weight: "medium",
    color: "text"
  }, trend.contributors.length) : /*#__PURE__*/React.createElement(Text, null, "-"))), /*#__PURE__*/React.createElement(Grid.Cell, {
    justify: "end",
    style: {
      textAlign: "right"
    }
  }, /*#__PURE__*/React.createElement(Text, {
    fontSize: 3,
    color: "textSecondary",
    display: "block",
    s: {
      mb: 2
    }
  }, "Sources"), /*#__PURE__*/React.createElement(Box, {
    align: "center",
    justify: "end"
  }, trend.sources.slice(0, 3).map(source => /*#__PURE__*/React.createElement(StyledSource, {
    key: source,
    s: {
      mx: -1
    },
    width: "1.8rem",
    height: "1.8rem",
    border: "rounded",
    src: lists.common.apps.logos(theme)[source]
  })), trend.sources.length > 3 && /*#__PURE__*/React.createElement(Cap, null, "+", trend.sources.length - 3)))), /*#__PURE__*/React.createElement(Contributions, null, error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), /*#__PURE__*/React.createElement(Text, {
    color: theme.colors.primary.main,
    fontSize: 3,
    weight: "semiBold",
    display: "block"
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "union",
    s: {
      mr: 1.5,
      mt: -0.5
    },
    color: theme.colors.primary.main
  }), trend.footprintCount, " contributions"), !hideActions && /*#__PURE__*/React.createElement(Button, {
    onClick: e => {
      e.stopPropagation();
      handleFollowClick();
    },
    onMouseEnter: () => setHoverFollow(true),
    onMouseLeave: () => setHoverFollow(false),
    color: trend.theme.topic?.isFollowed ? "translucent" : "primary",
    display: "block",
    fontSize: 3.5,
    loading: followStatus === StatusStateEnum.LOADING,
    s: {
      py: 2,
      pl: 4,
      pr: trend.theme.topic?.isFollowed ? 7 : 4,
      mt: 3
    },
    outline: false,
    maxWidth: "150px",
    textAlign: "center",
    fullWidth: true
  }, trend.theme.topic?.isFollowed && /*#__PURE__*/React.createElement(Icon, {
    src: "tick",
    s: {
      mr: 2
    }
  }), trend.theme.topic?.isFollowed ? hoverFollow ? t("TopicTrendCard.unfollow") : t("TopicTrendCard.following") : t("TopicTrendCard.follow"))));
};
export default TrendingTopicCard;